import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { readFileSync, writeFileSync, existsSync, realpathSync, lstatSync } from 'fs';
import { execSync } from 'child_process';
import path from 'path';
import { jsonCoerce, boolCoerce } from '../coerce.js';

/**
 * stackwright_pro_setup_packages MCP tool
 *
 * Ensures pro packages are present in a project's package.json.
 * Used by the Pro Foreman Otter to bootstrap dependencies before
 * specialist otters run — enabling the otter raft's IoC dependency
 * declaration pattern.
 *
 * Merge semantics: NEVER overwrites an existing version pin.
 * If a package is already declared at any version, it is skipped.
 * This preserves intentional version pinning by developers.
 */

interface PackageJson {
  dependencies?: Record<string, string>;
  devDependencies?: Record<string, string>;
  scripts?: Record<string, string>;
  [key: string]: unknown;
}

interface SetupPackagesResult {
  success: boolean;
  added: string[];
  skipped: string[];
  scriptsAdded: string[];
  installed: boolean;
  packageJsonPath: string;
  error?: string;
  installError?: string;
}

// ---------------------------------------------------------------------------
// Baseline dependencies moved from foreman prompt into version-controlled code
// ---------------------------------------------------------------------------

const BASELINE_DEPS: Record<string, string> = {
  '@stackwright-pro/mcp': 'latest',
  '@stackwright-pro/otters': 'latest',
  '@stackwright-pro/openapi': 'latest',
  '@stackwright-pro/auth': 'latest',
  '@stackwright-pro/auth-nextjs': 'latest',
  zod: '^3.23.0',
};

const BASELINE_DEV_DEPS: Record<string, string> = {
  '@stoplight/prism-cli': '^5.14.2',
};

export function registerPackageTools(server: McpServer): void {
  server.tool(
    'stackwright_pro_setup_packages',
    "Ensures pro packages are present in a project's package.json. " +
      'Safe to call multiple times — never overwrites existing version pins. ' +
      'Use this to bootstrap dependencies before specialist otters run. ' +
      'Pass includeBaseline: true to automatically include all required @stackwright-pro/* baseline dependencies. ' +
      'Safe to call on existing projects — never overwrites pinned versions.',
    {
      // FIX 3 (B-new-1): Zod v4 requires two-arg z.record(keySchema, valueSchema)
      packages: jsonCoerce(z.record(z.string(), z.string()).optional().default({})).describe(
        'Dependencies to add. Record<packageName, version>. e.g. { "@stackwright-pro/auth": "latest" }. Omit or pass {} when using includeBaseline: true.'
      ),
      devPackages: jsonCoerce(z.record(z.string(), z.string()).optional()).describe(
        'devDependencies to add. Same format as packages.'
      ),
      scripts: jsonCoerce(z.record(z.string(), z.string()).optional()).describe(
        'npm scripts to add. Only adds if key does not already exist.'
      ),
      targetDir: z
        .string()
        .optional()
        .describe(
          'Project directory containing package.json. Defaults to process.cwd(). Must be an absolute path within the current working directory.'
        ),
      runInstall: boolCoerce(z.boolean().optional().default(true)).describe(
        'Run pnpm install after writing package.json. Defaults to true. Pass boolean true/false.'
      ),
      includeBaseline: boolCoerce(z.boolean().optional().default(false)).describe(
        'When true, automatically merges BASELINE_DEPS and BASELINE_DEV_DEPS before applying packages/devPackages args. ' +
          'Safe to call on existing projects — never overwrites pinned versions. Pass boolean true/false.'
      ),
    },
    async ({ packages, devPackages, scripts, targetDir, runInstall, includeBaseline }) => {
      // Merge baseline deps when requested — existing "never overwrite" semantics
      // in setupPackages mean this is safe to call on any project.
      const mergedPackages: Record<string, string> = includeBaseline
        ? { ...BASELINE_DEPS, ...packages }
        : { ...packages };

      const mergedDevPackages: Record<string, string> | undefined = includeBaseline
        ? { ...BASELINE_DEV_DEPS, ...(devPackages ?? {}) }
        : devPackages;

      // exactOptionalPropertyTypes: true means we cannot pass `undefined` explicitly
      // for optional fields — conditionally spread them instead.
      const result = setupPackages({
        packages: mergedPackages,
        runInstall,
        ...(mergedDevPackages !== undefined ? { devPackages: mergedDevPackages } : {}),
        ...(scripts !== undefined ? { scripts } : {}),
        ...(targetDir !== undefined ? { targetDir } : {}),
      });

      const statusLine = result.success
        ? `✅ package.json updated: ${result.packageJsonPath}`
        : `❌ Failed: ${result.error}`;

      const lines = [
        statusLine,
        '',
        result.added.length > 0
          ? `Added (${result.added.length}): ${result.added.join(', ')}`
          : 'Added: none',
        result.skipped.length > 0
          ? `Skipped/already present (${result.skipped.length}): ${result.skipped.join(', ')}`
          : 'Skipped: none',
        result.scriptsAdded.length > 0
          ? `Scripts added (${result.scriptsAdded.length}): ${result.scriptsAdded.join(', ')}`
          : 'Scripts added: none',
        `pnpm install: ${result.installed ? 'ran successfully' : result.success && runInstall ? 'failed (non-fatal)' : 'skipped'}`,
      ];

      return {
        content: [
          {
            type: 'text' as const,
            text: lines.join('\n'),
          },
          {
            type: 'text' as const,
            text: JSON.stringify(result),
          },
        ],
      };
    }
  );
}

/**
 * Core implementation — extracted so it can be tested directly without
 * going through the MCP server machinery. Keeping I/O at the edges.
 */
export function setupPackages(opts: {
  packages: Record<string, string>;
  devPackages?: Record<string, string>;
  scripts?: Record<string, string>;
  targetDir?: string;
  runInstall: boolean;
}): SetupPackagesResult {
  const emptyResult: Omit<SetupPackagesResult, 'success' | 'error'> = {
    added: [],
    skipped: [],
    scriptsAdded: [],
    installed: false,
    packageJsonPath: '',
  };

  try {
    // -------------------------------------------------------------------------
    // Security: path traversal guard
    // -------------------------------------------------------------------------
    const cwd = process.cwd();
    const resolvedTarget = opts.targetDir ? path.resolve(opts.targetDir) : cwd;

    // Ensure resolved path stays within cwd — no sneaky ../../etc tricks
    const cwdWithSep = cwd.endsWith(path.sep) ? cwd : cwd + path.sep;
    if (resolvedTarget !== cwd && !resolvedTarget.startsWith(cwdWithSep)) {
      return {
        success: false,
        added: [],
        skipped: [],
        scriptsAdded: [],
        installed: false,
        packageJsonPath: '',
        // FIX 5 (M-4): do not leak absolute paths in error messages
        error: `Path traversal rejected: target directory is outside the allowed working directory`,
      };
    }

    // -------------------------------------------------------------------------
    // Find package.json (existence check before symlink resolution)
    // -------------------------------------------------------------------------
    const preResolvePackageJsonPath = path.join(resolvedTarget, 'package.json');

    if (!existsSync(preResolvePackageJsonPath)) {
      return {
        success: false,
        ...emptyResult,
        error: `No package.json found in ${resolvedTarget}`,
      };
    }

    // -------------------------------------------------------------------------
    // Security: directory-level symlink bypass guard (B2)
    // -------------------------------------------------------------------------
    let realTarget: string;
    try {
      realTarget = realpathSync(resolvedTarget);
    } catch {
      return {
        success: false,
        added: [],
        skipped: [],
        scriptsAdded: [],
        installed: false,
        packageJsonPath: '',
        error: `Could not resolve real path of target directory`,
      };
    }

    const realCwd = realpathSync(cwd);
    const realCwdWithSep = realCwd.endsWith(path.sep) ? realCwd : realCwd + path.sep;
    if (realTarget !== realCwd && !realTarget.startsWith(realCwdWithSep)) {
      return {
        success: false,
        added: [],
        skipped: [],
        scriptsAdded: [],
        installed: false,
        packageJsonPath: '',
        // FIX 5 (M-4): do not leak absolute paths in error messages
        error: `Path traversal rejected: target directory resolved to a location outside the allowed working directory`,
      };
    }

    // -------------------------------------------------------------------------
    // Security: file-level symlink guard at package.json (C-1 / FIX 1)
    // -------------------------------------------------------------------------
    // Reconstruct package.json path from the symlink-resolved target directory.
    // This prevents readFileSync / writeFileSync from following a file-level
    // symlink planted at <targetDir>/package.json.
    const realPackageJsonPath = path.join(realTarget, 'package.json');

    const pkgStat = lstatSync(realPackageJsonPath);
    if (pkgStat.isSymbolicLink()) {
      return {
        ...emptyResult,
        success: false,
        error: `package.json is a symlink — refusing to read or write`,
      };
    }

    // -------------------------------------------------------------------------
    // Security: package name validation (C-2C / L-1)
    // -------------------------------------------------------------------------
    // npm package name spec: optional @scope/, then lowercase alphanumeric + hyphens/dots/underscores
    const VALID_PKG_NAME_RE = /^(@[a-z0-9][a-z0-9\-._~]*\/)?[a-z0-9][a-z0-9\-._~]*$/;
    const allPkgNames = [
      ...Object.keys(opts.packages ?? {}),
      ...Object.keys(opts.devPackages ?? {}),
    ];
    for (const pkg of allPkgNames) {
      if (!VALID_PKG_NAME_RE.test(pkg)) {
        return {
          ...emptyResult,
          success: false,
          error: `Invalid package name: '${pkg}' — must match npm package name specification`,
        };
      }
    }

    // -------------------------------------------------------------------------
    // Security: version string allowlist (C-2B / FIX 2B)
    // -------------------------------------------------------------------------
    // Allows: semver ranges (^1.0.0, ~2.1, >=3, 1.x, *), workspace:*, latest/next/beta/alpha/canary/rc
    // Blocks: git+https://, file:, npm:evil@, http://, ssh://, etc.
    const SAFE_VERSION_RE =
      /^(workspace:[*^~]?|\*|latest|next|beta|alpha|canary|rc|[~^><=*|, ]*[\d.x*][\d.x*\-+a-zA-Z.~^><=*|, ]*)$/;
    const allVersionEntries = [
      ...Object.entries(opts.packages ?? {}),
      ...Object.entries(opts.devPackages ?? {}),
    ];
    for (const [pkg, version] of allVersionEntries) {
      if (!SAFE_VERSION_RE.test(version.trim())) {
        return {
          ...emptyResult,
          success: false,
          error: `Unsafe version specifier for '${pkg}': '${version}' — only semver ranges, workspace:*, and dist-tags (latest/next/beta) are permitted`,
        };
      }
    }

    // -------------------------------------------------------------------------
    // Security: lifecycle script key blocklist (C-2A / FIX 2A)
    // -------------------------------------------------------------------------
    const BLOCKED_LIFECYCLE_KEYS = new Set([
      'preinstall',
      'install',
      'postinstall',
      'prepare',
      'prepublish',
      'prepublishOnly',
      'prepack',
      'postpack',
      'dependencies', // pnpm hook key
    ]);
    if (opts.scripts) {
      for (const key of Object.keys(opts.scripts)) {
        if (BLOCKED_LIFECYCLE_KEYS.has(key)) {
          return {
            ...emptyResult,
            success: false,
            error: `Blocked lifecycle script key: '${key}' — writing npm lifecycle hooks is not permitted`,
          };
        }
      }
    }

    // -------------------------------------------------------------------------
    // Read & parse
    // -------------------------------------------------------------------------
    const raw = readFileSync(realPackageJsonPath, 'utf8');

    // W2: Validate package.json structure with Zod before touching anything
    const PackageJsonSchema = z
      .object({
        // Zod v4: z.record(keySchema, valueSchema) — two-arg form required
        dependencies: z.record(z.string(), z.string()).optional(),
        devDependencies: z.record(z.string(), z.string()).optional(),
        scripts: z.record(z.string(), z.string()).optional(),
      })
      .passthrough();

    const schemaResult = PackageJsonSchema.safeParse(JSON.parse(raw));
    if (!schemaResult.success) {
      return {
        ...emptyResult,
        success: false,
        error: `Invalid package.json structure: ${schemaResult.error.message}`,
      };
    }
    // FIX 4 (B-new-2): The schema has already validated the shape — the cast is earned.
    // passthrough() + noUncheckedIndexedAccess would otherwise broaden field types to unknown.
    const parsed = schemaResult.data as PackageJson;

    // -------------------------------------------------------------------------
    // Merge dependencies — NEVER overwrite existing version pins
    // -------------------------------------------------------------------------
    const added: string[] = [];
    const skipped: string[] = [];
    const scriptsAdded: string[] = [];

    // Live Set grows as packages are written — prevents same pkg landing in both buckets (B4).
    // Uses `in` semantics (not falsy) so empty-string versions are treated as existing (B3).
    const claimedPkgs = new Set<string>([
      ...Object.keys(parsed.dependencies ?? {}),
      ...Object.keys(parsed.devDependencies ?? {}),
    ]);

    // Regular dependencies
    parsed.dependencies = parsed.dependencies ?? {};
    for (const [pkg, version] of Object.entries(opts.packages)) {
      if (claimedPkgs.has(pkg)) {
        skipped.push(pkg);
      } else {
        parsed.dependencies[pkg] = version;
        claimedPkgs.add(pkg);
        added.push(pkg);
      }
    }

    // Dev dependencies
    if (opts.devPackages && Object.keys(opts.devPackages).length > 0) {
      parsed.devDependencies = parsed.devDependencies ?? {};
      for (const [pkg, version] of Object.entries(opts.devPackages)) {
        if (claimedPkgs.has(pkg)) {
          skipped.push(pkg);
        } else {
          parsed.devDependencies[pkg] = version;
          claimedPkgs.add(pkg);
          added.push(pkg);
        }
      }
    }

    // Scripts — same never-overwrite semantics
    if (opts.scripts && Object.keys(opts.scripts).length > 0) {
      parsed.scripts = parsed.scripts ?? {};
      for (const [key, value] of Object.entries(opts.scripts)) {
        if (parsed.scripts[key] === undefined) {
          parsed.scripts[key] = value;
          scriptsAdded.push(key);
        }
      }
    }

    // -------------------------------------------------------------------------
    // Write back — use realPackageJsonPath (symlink-resolved) for all I/O
    // -------------------------------------------------------------------------
    writeFileSync(realPackageJsonPath, JSON.stringify(parsed, null, 2) + '\n');

    // -------------------------------------------------------------------------
    // Run pnpm install (optional, non-fatal on failure)
    // -------------------------------------------------------------------------
    let installed = false;
    let installError: string | undefined;

    if (opts.runInstall) {
      try {
        execSync('pnpm install', { cwd: realTarget, stdio: 'pipe', timeout: 60_000 });
        installed = true;
      } catch (err) {
        // Don't blow up the whole tool — install failure is recoverable.
        // The caller can re-run or handle manually.
        installed = false;
        const spawnErr = err as { stderr?: Buffer; message?: string };
        installError =
          spawnErr.stderr?.toString().trim() ||
          (err instanceof Error ? err.message : 'unknown error');
      }
    }

    return {
      success: true,
      added,
      skipped,
      scriptsAdded,
      installed,
      packageJsonPath: realPackageJsonPath,
      ...(installError !== undefined ? { installError } : {}),
    };
  } catch (err) {
    return {
      ...emptyResult,
      success: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}
